// src/pages/Leaderboard2026.js
// 2026 Season Leaderboard - Full featured with race-by-race scores, marquee badges, and smack talk

import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import apiClient from '../utils/apiClient';

// Custom Sheriff Star Component for Texas
const SheriffStar = ({ size = "w-4 h-4" }) => (
  <svg
    className={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 2L15.09 8.26L22 9L17 14.14L18.18 21L12 17.77L5.82 21L7 14.14L2 9L8.91 8.26L12 2Z"
      fill="#FFD700"
    />
  </svg>
);

// Generic Season Champion Badge - Gold Shield with Crown
const ChampionBadge = ({
  season,
  size = "w-6 h-7"
}) => (
  <svg
    className={size}
    viewBox="0 0 30 32"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>
      {season} Season Champion
    </title>

    {/* Crown */}
    <path
      d="M5 11L8 5L11.5 9L15 4L18.5 9L22 5L25 11V13H5V11Z"
      fill="#FFD700"
      stroke="#B8860B"
      strokeWidth="0.5"
    />

    <circle
      cx="8"
      cy="8"
      r="0.8"
      fill="#B8860B"
    />

    <circle
      cx="15"
      cy="5.5"
      r="0.8"
      fill="#B8860B"
    />

    <circle
      cx="22"
      cy="8"
      r="0.8"
      fill="#B8860B"
    />

    {/* Shield body */}
    <path
      d="M5 13H25V22C25 22 24 28 15 30C6 28 5 22 5 22V13Z"
      fill="#FFD700"
      stroke="#B8860B"
      strokeWidth="0.8"
    />

    {/* Inner shield */}
    <path
      d="M7 15H23V21C23 21 22 26 15 28C8 26 7 21 7 21V15Z"
      fill="#FFF8DC"
    />

    {/* Season year (last 2 digits) */}
    <text
      x="15"
      y="24.5"
      textAnchor="middle"
      fontFamily="system-ui, sans-serif"
      fontSize="10"
      fontWeight="900"
      fill="#B8860B"
    >
      {String(season).slice(-2)}
    </text>
  </svg>
);

// Comment Message Component
const CommentMessage = ({
  message,
  currentUser,
  onEdit,
  onDelete
}) => {
  const [isEditing, setIsEditing] =
    useState(false);

  const [editText, setEditText] =
    useState(message.message);

  const [isHovered, setIsHovered] =
    useState(false);

  const isOwnMessage =
    currentUser &&
    message.user &&
    message.user._id ===
      currentUser.id;

  const canEdit =
    isOwnMessage &&
    new Date() -
      new Date(message.timestamp) <
      5 * 60 * 1000;

  const handleEdit = async () => {
    if (
      editText.trim() !==
      message.message
    ) {
      await onEdit(
        message._id,
        editText.trim()
      );
    }

    setIsEditing(false);
  };

  const formatTime = timestamp => {
    const date =
      new Date(timestamp);

    const now =
      new Date();

    const diffMs =
      now - date;

    const diffMins =
      Math.floor(
        diffMs / 60000
      );

    const diffHours =
      Math.floor(
        diffMins / 60
      );

    const diffDays =
      Math.floor(
        diffHours / 24
      );

    if (diffMins < 1) {
      return 'now';
    }

    if (diffMins < 60) {
      return `${diffMins}m ago`;
    }

    if (diffHours < 24) {
      return `${diffHours}h ago`;
    }

    if (diffDays < 7) {
      return `${diffDays}d ago`;
    }

    return date.toLocaleDateString();
  };

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
      onMouseEnter={() =>
        setIsHovered(true)
      }
      onMouseLeave={() =>
        setIsHovered(false)
      }
    >
      <div className="flex items-start justify-between mb-2">

        <div className="flex items-center space-x-3">

          <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium">
            {message.username
              ?.charAt(0)
              .toUpperCase() ||
              'U'}
          </div>

          <div>
            <span className="font-semibold text-gray-900">
              {message.username}
            </span>

            <span className="text-sm text-gray-500 ml-2">
              {formatTime(
                message.timestamp
              )}

              {message.edited && (
                <span className="ml-1 italic">
                  (edited)
                </span>
              )}
            </span>
          </div>

        </div>

        {isHovered &&
          canEdit && (
            <div className="flex space-x-2">

              <button
                onClick={() =>
                  setIsEditing(true)
                }
                className="text-sm text-blue-600 hover:text-blue-800 px-2 py-1 rounded hover:bg-blue-50"
              >
                Edit
              </button>

              <button
                onClick={() =>
                  onDelete(
                    message._id
                  )
                }
                className="text-sm text-red-600 hover:text-red-800 px-2 py-1 rounded hover:bg-red-50"
              >
                Delete
              </button>

            </div>
          )}
      </div>

      {isEditing ? (
        <div className="space-y-2">

          <textarea
            value={editText}
            onChange={e =>
              setEditText(
                e.target.value
              )
            }
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white resize-none"
            rows="3"
            maxLength={500}
            autoFocus
          />

          <div className="flex justify-between items-center">

            <span className="text-xs text-gray-500">
              {editText.length}/500 characters
            </span>

            <div className="flex space-x-2">

              <button
                onClick={
                  handleEdit
                }
                className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600"
              >
                Save
              </button>

              <button
                onClick={() =>
                  setIsEditing(
                    false
                  )
                }
                className="px-3 py-1 bg-gray-500 text-white text-sm rounded hover:bg-gray-600"
              >
                Cancel
              </button>

            </div>
          </div>
        </div>
      ) : (
        <p className="text-gray-800 whitespace-pre-wrap break-words leading-relaxed">
          {message.message}
        </p>
      )}
    </div>
  );
};

// Comments / Smack Talk Component
const Comments = ({
  currentUser
}) => {
  const [messages, setMessages] =
    useState([]);

  const [
    newMessage,
    setNewMessage
  ] = useState('');

  const [loading, setLoading] =
    useState(false);

  const [sending, setSending] =
    useState(false);

  const [error, setError] =
    useState('');

  const [success, setSuccess] =
    useState('');

  const fetchMessages =
    useCallback(async () => {
      try {
        setLoading(true);
        setError('');

        const response =
          await apiClient.get(
            '/api/chat/messages?page=1&limit=50'
          );

        const sortedMessages =
          (
            response.data
              .messages || []
          ).reverse();

        setMessages(
          sortedMessages
        );

        setSuccess(
          `✅ Loaded ${
            response.data
              .messages?.length ||
            0
          } comments`
        );

      } catch (error) {
        console.error(
          'Error fetching messages:',
          error
        );

        if (
          error.response?.status ===
          401
        ) {
          setError(
            'Please log in to view comments'
          );
        } else {
          setError(
            'Failed to load comments. Please try again.'
          );
        }
      } finally {
        setLoading(false);
      }
    }, []);

  const sendMessage = async () => {
    if (
      !newMessage.trim() ||
      sending
    ) {
      return;
    }

    try {
      setSending(true);
      setError('');

      const response =
        await apiClient.post(
          '/api/chat/messages',
          {
            message:
              newMessage.trim()
          }
        );

      setMessages(prev => [
        response.data,
        ...prev
      ]);

      setNewMessage('');

      setSuccess(
        '✅ Comment posted successfully!'
      );

    } catch (error) {
      console.error(
        'Error sending message:',
        error
      );

      if (
        error.response?.status ===
        401
      ) {
        setError(
          'Please log in to post comments'
        );
      } else if (
        error.response?.status ===
        429
      ) {
        setError(
          'Too many messages. Please wait before posting again.'
        );
      } else {
        setError(
          error.response?.data
            ?.message ||
            'Failed to send comment'
        );
      }
    } finally {
      setSending(false);
    }
  };

  const editMessage =
    async (
      messageId,
      newText
    ) => {
      try {
        setError('');

        const response =
          await apiClient.put(
            `/api/chat/messages/${messageId}`,
            {
              message:
                newText
            }
          );

        setMessages(prev =>
          prev.map(msg =>
            msg._id === messageId
              ? response.data
              : msg
          )
        );

        setSuccess(
          '✅ Comment updated!'
        );

      } catch (error) {
        console.error(
          'Error editing message:',
          error
        );

        setError(
          error.response?.data
            ?.message ||
            'Failed to edit comment'
        );
      }
    };

  const deleteMessage =
    async messageId => {
      if (
        !window.confirm(
          'Are you sure you want to delete this comment?'
        )
      ) {
        return;
      }

      try {
        setError('');

        await apiClient.delete(
          `/api/chat/messages/${messageId}`
        );

        setMessages(prev =>
          prev.filter(
            msg =>
              msg._id !==
              messageId
          )
        );

        setSuccess(
          '✅ Comment deleted!'
        );

      } catch (error) {
        console.error(
          'Error deleting message:',
          error
        );

        setError(
          error.response?.data
            ?.message ||
            'Failed to delete comment'
        );
      }
    };

  useEffect(() => {
    fetchMessages();

    const interval =
      setInterval(
        fetchMessages,
        30000
      );

    return () =>
      clearInterval(interval);

  }, [fetchMessages]);

  useEffect(() => {
    if (success) {
      const timer =
        setTimeout(
          () =>
            setSuccess(''),
          3000
        );

      return () =>
        clearTimeout(timer);
    }
  }, [success]);

  return (
    <div
      id="comments-section"
      className="bg-white rounded-lg shadow-md p-6"
    >

      <div className="flex items-center justify-between mb-6">

        <h2 className="text-2xl font-bold text-gray-800">
          Smack talk
        </h2>

        <div className="flex items-center space-x-4">

          <span className="text-sm text-gray-500">
            {messages.length}{' '}
            comments
          </span>

          <button
            onClick={
              fetchMessages
            }
            disabled={loading}
            className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400"
          >
            {loading
              ? 'Loading...'
              : '🔄 Refresh'}
          </button>

        </div>
      </div>

      {success && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded-lg">
          {success}
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {/* Comment Form */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">

        <div className="flex items-start space-x-3">

          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium flex-shrink-0">
            {currentUser
              ?.username
              ?.charAt(0)
              .toUpperCase() ||
              'U'}
          </div>

          <div className="flex-1">

            <textarea
              value={
                newMessage
              }
              onChange={e =>
                setNewMessage(
                  e.target.value
                )
              }
              placeholder="SLet it rip..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 bg-white resize-none"
              rows="3"
              maxLength={500}
              disabled={
                sending
              }
            />

            <div className="flex justify-between items-center mt-2">

              <span className="text-xs text-gray-500">
                {newMessage.length}/500 characters
              </span>

              <button
                onClick={
                  sendMessage
                }
                disabled={
                  !newMessage.trim() ||
                  sending
                }
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {sending
                  ? 'Posting...'
                  : 'Post Comment'}
              </button>

            </div>
          </div>
        </div>
      </div>

      {/* Comments List */}
      <div className="space-y-4">

        {loading &&
          messages.length ===
            0 && (
            <div className="text-center text-gray-500 py-8">
              Loading comments...
            </div>
          )}

        {messages.length ===
          0 &&
          !loading && (
            <div className="text-center text-gray-500 py-8">

              <div className="text-4xl mb-2">
                💬
              </div>

              <p>
                No comments yet.
                Be the first to
                share your
                thoughts!
              </p>

            </div>
          )}

        {messages.map(
          message => (
            <CommentMessage
              key={
                message._id
              }
              message={
                message
              }
              currentUser={
                currentUser
              }
              onEdit={
                editMessage
              }
              onDelete={
                deleteMessage
              }
            />
          )
        )}

      </div>
    </div>
  );
};

function Leaderboard2026() {
  const { user } =
    useContext(AuthContext);

  const location =
    useLocation();

  const [users, setUsers] =
    useState([]);

  const [races, setRaces] =
    useState([]);

  const [
    raceScores,
    setRaceScores
  ] = useState({});

  const [
    champions,
    setChampions
  ] = useState({});

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState(null);

  const [
    showScoringRules,
    setShowScoringRules
  ] = useState(false);

  const [
    currentUser,
    setCurrentUser
  ] = useState(null);

  // Define marquee races with their badge info
  const marqueeRaces = {
    australia: {
      name:
        'Australian GP',
      badge: '🦘',
      color:
        'text-yellow-700',
      bgColor:
        'bg-yellow-100',
      title:
        'Australian Kangaroo',
      isCustom: false
    },

    monaco: {
      name:
        'Monaco GP',
      badge: '🎲',
      color:
        'text-blue-600',
      bgColor:
        'bg-blue-100',
      title:
        'Monaco Dice',
      isCustom: false
    },

    silverstone: {
      name:
        'British GP',
      badge: '⚔️',
      color:
        'text-red-600',
      bgColor:
        'bg-red-100',
      title:
        'Silverstone Sword',
      isCustom: false
    },

    spa: {
      name:
        'Spa GP',
      badge: '🌲',
      color:
        'text-green-600',
      bgColor:
        'bg-green-100',
      title:
        'Spa Forest',
      isCustom: false
    },

    monza: {
      name:
        'Monza GP',
      badge: '🏛️',
      color:
        'text-green-600',
      bgColor:
        'bg-green-100',
      title:
        'Monza Temple of Speed',
      isCustom: false
    },

    cota: {
      name:
        'COTA GP',
      badge: (
        <SheriffStar size="w-4 h-4" />
      ),
      color:
        'text-yellow-600',
      bgColor:
        'bg-yellow-800',
      title:
        'Texas Sheriff',
      isCustom: true
    },

    'abu-dhabi': {
      name:
        'Abu Dhabi GP',
      badge: '🐪',
      color:
        'text-orange-600',
      bgColor:
        'bg-orange-100',
      title:
        'Abu Dhabi Desert Camel',
      isCustom: false
    }
  };

  // Check if a race is a marquee race
  const getMarqueeRaceInfo =
    raceName => {
      const normalizedName =
        raceName.toLowerCase();

      if (
        normalizedName.includes(
          'australia'
        ) ||
        normalizedName.includes(
          'melbourne'
        )
      ) {
        return marqueeRaces.australia;
      }

      if (
        normalizedName.includes(
          'monaco'
        )
      ) {
        return marqueeRaces.monaco;
      }

      if (
        normalizedName.includes(
          'british'
        ) ||
        normalizedName.includes(
          'silverstone'
        )
      ) {
        return marqueeRaces.silverstone;
      }

      if (
        normalizedName.includes(
          'belgian'
        ) ||
        normalizedName.includes(
          'belgium'
        )
      ) {
        return marqueeRaces.spa;
      }

      if (
        normalizedName.includes(
          'monza'
        ) ||
        normalizedName.includes(
          'italian'
        )
      ) {
        return marqueeRaces.monza;
      }

      if (
        normalizedName.includes(
          'austin'
        ) ||
        normalizedName.includes(
          'cota'
        ) ||
        normalizedName.includes(
          'united states'
        )
      ) {
        return marqueeRaces.cota;
      }

      if (
        normalizedName.includes(
          'abu dhabi'
        )
      ) {
        return marqueeRaces[
          'abu-dhabi'
        ];
      }

      return null;
    };

  // Get user's badges based on race wins
  const getUserBadges =
    userId => {
      const badges = [];

      races.forEach(
        race => {
          const marqueeInfo =
            getMarqueeRaceInfo(
              race.raceName
            );

          if (
            marqueeInfo &&
            raceScores[
              race._id
            ]
          ) {
            const raceResults =
              Object.entries(
                raceScores[
                  race._id
                ]
              )
                .map(
                  ([
                    uid,
                    score
                  ]) => ({
                    userId:
                      uid,
                    score
                  })
                )
                .sort(
                  (a, b) =>
                    b.score -
                    a.score
                );

            if (
              raceResults.length >
                0 &&
              raceResults[0]
                .userId ===
                userId &&
              raceResults[0]
                .score > 0
            ) {
              badges.push(
                marqueeInfo
              );
            }
          }
        }
      );

      return badges;
    };

  // Get a user's score for a specific race
  const getUserRaceScore =
    (
      userId,
      raceId
    ) => {
      if (
        raceScores[
          raceId
        ] &&
        raceScores[
          raceId
        ][userId] !==
          undefined
      ) {
        return raceScores[
          raceId
        ][userId];
      }

      return 0;
    };

  // Calculate user statistics
  const calculateUserStats =
    userId => {
      let totalPoints = 0;

      let racesParticipated =
        0;

      races.forEach(
        race => {
          const score =
            getUserRaceScore(
              userId,
              race._id
            );

          if (score > 0) {
            totalPoints +=
              score;

            racesParticipated++;
          }
        }
      );

      const averageScore =
        racesParticipated > 0
          ? totalPoints /
            racesParticipated
          : 0;

      return {
        totalPoints,
        racesParticipated,
        averageScore:
          Math.round(
            averageScore *
              100
          ) / 100
      };
    };

  // Get current user info from token
  useEffect(() => {
    const token =
      localStorage.getItem(
        'token'
      );

    if (token) {
      try {
        const payload =
          JSON.parse(
            atob(
              token.split(
                '.'
              )[1]
            )
          );

        setCurrentUser({
          id:
            payload.userId,
          username:
            payload.username ||
            'User'
        });

      } catch (error) {
        console.error(
          'Error parsing token:',
          error
        );
      }
    }
  }, []);

  // Scroll to comments section when #comments hash is present
  useEffect(() => {
    if (
      location.hash ===
        '#comments' &&
      !loading
    ) {
      setTimeout(() => {
        const el =
          document.getElementById(
            'comments-section'
          );

        if (el) {
          el.scrollIntoView({
            behavior:
              'smooth',
            block:
              'start'
          });
        }
      }, 300);
    }
  }, [
    location.hash,
    loading
  ]);

  // Load the entire leaderboard in ONE request
  useEffect(() => {
    const fetchData =
      async () => {
        try {
          setLoading(true);
          setError(null);

          const response =
            await apiClient.get(
              '/api/scores/leaderboard?season=2026'
            );

          const data =
            response.data ||
            {};

          const leaderboardData =
            Array.isArray(
              data.leaderboard
            )
              ? data.leaderboard
              : [];

          const racesData =
            Array.isArray(
              data.races
            )
              ? data.races
              : [];

          const raceScoresData =
            data.raceScores &&
            typeof data.raceScores ===
              'object'
              ? data.raceScores
              : {};

          const championsData =
            data.champions &&
            typeof data.champions ===
              'object'
              ? data.champions
              : {};

          // Preserve existing behaviour:
          // don't display users who have never scored.
          const activeUsers =
            leaderboardData.filter(
              entry =>
                entry.totalPoints >
                0
            );

          setUsers(
            activeUsers
          );

          setRaces(
            racesData
          );

          setRaceScores(
            raceScoresData
          );

          setChampions(
            championsData
          );

        } catch (err) {
          setError(
            'Failed to load data'
          );

          console.error(
            'Error fetching leaderboard data:',
            err
          );
        } finally {
          setLoading(
            false
          );
        }
      };

    fetchData();
  }, []);

  const INACTIVE_THRESHOLD =
    0.8;

  const enhancedUsers =
    users
      .map(u => ({
        ...u,

        stats:
          calculateUserStats(
            u.id ||
              u.userId
          ),

        badges:
          getUserBadges(
            u.id ||
              u.userId
          )
      }))
      .sort((a, b) => {
        const totalRaces =
          races.length;

        if (
          totalRaces === 0
        ) {
          return 0;
        }

        const aParticipation =
          a.stats
            .racesParticipated /
          totalRaces;

        const bParticipation =
          b.stats
            .racesParticipated /
          totalRaces;

        const aInactive =
          aParticipation <
          (
            1 -
            INACTIVE_THRESHOLD
          );

        const bInactive =
          bParticipation <
          (
            1 -
            INACTIVE_THRESHOLD
          );

        if (
          aInactive &&
          !bInactive
        ) {
          return 1;
        }

        if (
          !aInactive &&
          bInactive
        ) {
          return -1;
        }

        if (
          aInactive &&
          bInactive
        ) {
          return 0;
        }

        if (
          b.stats
            .averageScore !==
          a.stats
            .averageScore
        ) {
          return (
            b.stats
              .averageScore -
            a.stats
              .averageScore
          );
        }

        if (
          b.stats
            .racesParticipated !==
          a.stats
            .racesParticipated
        ) {
          return (
            b.stats
              .racesParticipated -
            a.stats
              .racesParticipated
          );
        }

        return (
          b.stats
            .totalPoints -
          a.stats
            .totalPoints
        );
      });

  if (loading) {
    return (
      <div className="text-center py-10">
        Loading leaderboard...
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">

        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>

      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">

      <h1 className="text-3xl font-bold mb-2">
        2026 Season Leaderboard
      </h1>

      <p className="text-gray-400 mb-1">
        Compete throughout the F1 season
      </p>

      <p className="text-gray-400 text-sm mb-6">
        <sup>P</sup> - designates paid
      </p>

      {user &&
        (() => {
          const userEntry =
            enhancedUsers.find(
              u =>
                (
                  u.id ||
                  u.userId
                ) ===
                user.id
            );

          const rank =
            userEntry
              ? enhancedUsers.indexOf(
                  userEntry
                ) + 1
              : null;

          return rank ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">

              <p className="text-gray-800">

                <strong>
                  Your Current Rank:
                </strong>{' '}

                #{rank} with{' '}

                {
                  userEntry
                    .stats
                    .totalPoints
                }{' '}

                points (Avg:{' '}

                {
                  userEntry
                    .stats
                    .averageScore
                    .toFixed(1)
                }

                )

              </p>

            </div>
          ) : null;
        })()}

      <div className="bg-white rounded-lg shadow-md overflow-hidden overflow-x-auto mb-8">

        <table className="min-w-full divide-y divide-gray-200">

          <thead className="bg-gray-50">

            <tr>

              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-10 min-w-[60px]">
                Rank
              </th>

              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-[60px] bg-gray-50 z-10 min-w-[200px]">
                Username
              </th>

              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-[260px] bg-gray-50 z-10 min-w-[80px]">
                Avg Score
              </th>

              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-[340px] bg-gray-50 z-10 min-w-[80px]">
                Total Pts
              </th>

              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-[420px] bg-gray-50 z-10 min-w-[70px]">
                Races
              </th>

              {races.map(
                race => (
                  <th
                    key={
                      race._id
                    }
                    className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[60px]"
                  >
                    {
                      race
                        .circuit
                        ?.country ||
                      (
                        race.raceName
                          ? race.raceName.split(
                              ' '
                            )[0]
                          : 'Race'
                      )
                    }
                  </th>
                )
              )}

            </tr>

          </thead>

          <tbody className="bg-white divide-y divide-gray-200">

            {enhancedUsers.length ===
            0 ? (

              <tr>

                <td
                  colSpan={
                    5 +
                    races.length
                  }
                  className="px-6 py-8 text-center text-gray-600"
                >
                  No one has made predictions yet. Be the first to make your 2026 predictions!
                </td>

              </tr>

            ) : (

              enhancedUsers.map(
                (
                  u,
                  index
                ) => {
                  const userId =
                    u.id ||
                    u.userId;

                  const isCurrentUser =
                    user &&
                    userId ===
                      user.id;

                  const isInactive =
                    races.length >
                      0 &&
                    (
                      u.stats
                        .racesParticipated /
                      races.length
                    ) <
                      (
                        1 -
                        INACTIVE_THRESHOLD
                      );

                  return (
                    <tr
                      key={
                        userId ||
                        index
                      }
                      className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${isInactive ? 'bg-gray-100 text-gray-400' : ''} ${isCurrentUser ? 'ring-2 ring-blue-300 ring-inset' : ''}`}
                    >

                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 sticky left-0 bg-inherit z-10 font-medium text-center">
                        {index + 1}
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900 sticky left-[60px] bg-inherit z-10">

                        <div className="flex items-center space-x-2">

                          <span>

                            {u.username ||
                              u.email ||
                              'Unknown User'}

                            {u.hasPaid && (
                              <sup className="text-black ml-0.5 text-xs">
                                P
                              </sup>
                            )}

                          </span>

                          {Object.entries(
                            champions
                          ).map(
                            ([
                              season,
                              champ
                            ]) =>
                              champ.userId ===
                                (
                                  u.id ||
                                  u.userId
                                )?.toString() && (
                                <ChampionBadge
                                  key={
                                    season
                                  }
                                  season={
                                    season
                                  }
                                  size="w-5 h-6"
                                />
                              )
                          )}

                          {isCurrentUser && (
                            <span className="text-xs text-blue-600 font-medium">
                              (You)
                            </span>
                          )}

                          {isInactive && (
                            <span className="text-xs text-gray-500 italic">
                              (inactive)
                            </span>
                          )}

                          <div className="flex space-x-1">

                            {u.badges.map(
                              (
                                badge,
                                badgeIndex
                              ) => (
                                <div
                                  key={
                                    badgeIndex
                                  }
                                  className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${badge.bgColor} ${badge.color}`}
                                  title={`${badge.title} - ${badge.name} Winner`}
                                >
                                  {badge.isCustom ? (
                                    <span className="flex items-center">
                                      {badge.badge}
                                    </span>
                                  ) : (
                                    <span className="text-sm">
                                      {badge.badge}
                                    </span>
                                  )}
                                </div>
                              )
                            )}

                          </div>

                        </div>

                      </td>

                      <td className="px-4 py-4 whitespace-nowrap text-sm font-bold text-blue-600 sticky left-[260px] bg-inherit z-10 text-center">
                        {
                          u.stats
                            .averageScore
                            .toFixed(1)
                        }
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 sticky left-[340px] bg-inherit z-10 text-center">
                        {
                          u.stats
                            .totalPoints
                        }
                      </td>

                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-600 sticky left-[420px] bg-inherit z-10 text-center">
                        {
                          u.stats
                            .racesParticipated
                        }
                      </td>

                      {races.map(
                        race => {
                          const points =
                            getUserRaceScore(
                              userId,
                              race._id
                            );

                          return (
                            <td
                              key={`${userId}-${race._id}`}
                              className="px-3 py-4 whitespace-nowrap text-sm text-center"
                            >

                              <span
                                className={`font-medium ${
                                  points >
                                  0
                                    ? 'text-green-600'
                                    : 'text-gray-400'
                                }`}
                              >
                                {points >
                                0
                                  ? points
                                  : '-'}
                              </span>

                            </td>
                          );
                        }
                      )}

                    </tr>
                  );
                }
              )
            )}

          </tbody>

        </table>

      </div>

      {/* Marquee Race Badge Legend */}
      <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-4">

        <h3 className="text-sm font-medium text-blue-800 mb-2">
          Marquee Race Winner Badge Legend
        </h3>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-blue-700">

          {Object.entries(
            marqueeRaces
          ).map(
            ([
              key,
              badge
            ]) => (

              <div
                key={key}
                className="flex items-center space-x-2"
              >

                <span
                  className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${badge.bgColor} ${badge.color}`}
                >
                  {badge.isCustom ? (
                    <span className="flex items-center">
                      {badge.badge}
                    </span>
                  ) : (
                    <span className="text-sm">
                      {badge.badge}
                    </span>
                  )}
                </span>

                <span>
                  {badge.title}
                </span>

              </div>
            )
          )}

        </div>

        <div className="mt-3 text-xs text-blue-600">

          <p>
            <strong>
              Ranking System:
            </strong>{' '}
            Average score → Races participated → Total points
          </p>

          <p>
            Badges are awarded to winners of marquee races (Australia, Monaco, Silverstone, Spa, Monza, COTA, Abu Dhabi)
          </p>

        </div>

      </div>

      {/* Scoring Rules */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-l-4 border-blue-500 p-8 rounded-lg mb-8">

        <div className="flex justify-between items-center mb-6">

          <h3 className="text-xl font-bold text-gray-800">
            📊 2026 Scoring
          </h3>

          <button
            onClick={() =>
              setShowScoringRules(
                !showScoringRules
              )
            }
            className="text-blue-600 hover:text-blue-800 text-sm font-semibold"
          >
            {showScoringRules
              ? 'Hide Rules'
              : 'Show Rules'}
          </button>

        </div>

        {showScoringRules && (

          <div className="space-y-6">

            <div className="bg-white p-4 rounded border-l-4 border-blue-500">

              <h4 className="font-bold text-gray-800 mb-2">
                💡 How Scoring Works:
              </h4>

              <p className="text-sm text-gray-700">
                You earn the F1 championship points your selected driver actually earns. Double points for correct position. Triple points on podium when all 3 correct. 4× base for all 5 correct = 320 points.
              </p>

            </div>

            <div>

              <h4 className="font-bold text-gray-800 mb-3">
                Position Points (Feature Races)
              </h4>

              <div className="bg-white p-4 rounded border border-gray-200">

                <div className="grid grid-cols-10 gap-2 mb-2">

                  {[
                    '1st',
                    '2nd',
                    '3rd',
                    '4th',
                    '5th',
                    '6th',
                    '7th',
                    '8th',
                    '9th',
                    '10th'
                  ].map(
                    pos => (
                      <div
                        key={
                          pos
                        }
                        className="text-center text-xs font-semibold text-gray-600"
                      >
                        {pos}
                      </div>
                    )
                  )}

                </div>

                <div className="grid grid-cols-10 gap-2">

                  {[
                    25,
                    18,
                    15,
                    12,
                    10,
                    8,
                    6,
                    4,
                    2,
                    1
                  ].map(
                    (
                      pts,
                      idx
                    ) => (
                      <div
                        key={
                          idx
                        }
                        className="text-center"
                      >
                        <div className="font-bold text-blue-600">
                          {pts}
                        </div>
                      </div>
                    )
                  )}

                </div>

              </div>

            </div>

            <div>

              <h4 className="font-bold text-gray-800 mb-3">
                Position Bonuses
              </h4>

              <div className="space-y-3">

                <div className="bg-white p-3 rounded border-l-4 border-green-500">

                  <div className="font-semibold text-gray-800">
                    Correct Position (×2)
                  </div>

                  <p className="text-xs text-gray-600 mt-1">
                    When your predicted driver finishes in the position you selected, you get double points
                  </p>

                  <p className="text-xs text-green-600 mt-1">
                    Example: Predict P1 and finish P1 = 25 × 2 = 50 points
                  </p>

                </div>

                <div className="bg-white p-3 rounded border-l-4 border-purple-500">

                  <div className="font-semibold text-gray-800">
                    All 3 Podium Correct (×3)
                  </div>

                  <p className="text-xs text-gray-600 mt-1">
                    You get triple points on podium when all 3 are correct
                  </p>

                  <p className="text-xs text-purple-600 mt-1">
                    Example: P1 (50) + P2 (36) + P3 (30) + 58 bonus = 174 points
                  </p>

                </div>

                <div className="bg-white p-3 rounded border-l-4 border-orange-500">

                  <div className="font-semibold text-gray-800">
                    All 5 Correct (×4)
                  </div>

                  <p className="text-xs text-gray-600 mt-1">
                    You get 4× the base top 5 score
                  </p>

                  <p className="text-xs text-orange-600 mt-1">
                    Example: Base 80 → All correct = 320 points
                  </p>

                </div>

              </div>

            </div>

            <div className="bg-white p-4 rounded border-l-4 border-orange-500">

              <h4 className="font-bold text-gray-800 mb-2">
                Other Predictions (Fixed Points)
              </h4>

              <ul className="text-sm text-gray-700 space-y-1">

                <li>
                  <strong>
                    Feature Races:
                  </strong>{' '}
                  4 points each (if correct)
                </li>

                <li>
                  <strong>
                    Sprint Races:
                  </strong>{' '}
                  1 point each (if correct)
                </li>

                <li className="text-xs mt-2">
                  Applies to: Pole Position, Fastest Lap, First Retirement, Driver/Team Head-to-Head
                </li>

              </ul>

            </div>

          </div>
        )}

      </div>

      {/* Comments / Smack Talk Section */}
      <Comments
        currentUser={
          currentUser
        }
      />

    </div>
  );
}

export default Leaderboard2026;